import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { getImageUrlSync } from '../utils/imageProxy';
import { apiService } from '../services/api';
import { getCallStatusLabel, getCallStatusColor } from '../services/phone-dialer.service';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const getInitials = (name: string): string =>
  name.split(' ').map((n) => n.charAt(0)).join('').substring(0, 2).toUpperCase();

/**
 * Compact call bar shown at the top of the right pane (above the contact
 * list) when the in-call screen is minimized — tapping it re-expands to
 * PhoneCallScreen. Only rendered while a call is ringing/connected.
 */
export const PhoneCallMinimizedBar: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { phoneCallState, phoneCallDuration, phoneCallContact, hangupPhoneCall, dismissPhoneCall, setPhoneCallMinimized } = useAppContext();

  if (phoneCallState === 'idle') return null;

  const isConnected = phoneCallState === 'connected';
  const isUnresolvedTerminal = phoneCallState === 'cancelled' || phoneCallState === 'not-responded' || phoneCallState === 'failed';
  const name = phoneCallContact?.name || 'Unknown';
  const apiBaseURL = apiService.getBaseURL?.() ?? null;

  return (
    <TouchableOpacity style={styles.container} onPress={() => setPhoneCallMinimized(false)} activeOpacity={0.85}>
      {phoneCallContact?.avatar ? (
        <Image source={{ uri: getImageUrlSync(phoneCallContact.avatar, apiBaseURL) }} style={styles.avatar} />
      ) : (
        <View style={[styles.avatar, styles.avatarFallback]}>
          <Text style={styles.avatarInitials}>{getInitials(name)}</Text>
        </View>
      )}

      <View style={styles.info}>
        <Text style={styles.name} numberOfLines={1}>{name}</Text>
        <Text style={styles.status}>{isConnected ? formatDuration(phoneCallDuration) : getCallStatusLabel(phoneCallState)}</Text>
      </View>

      <View style={[styles.statusDot, { backgroundColor: getCallStatusColor(phoneCallState) }]} />

      {isUnresolvedTerminal ? (
        <TouchableOpacity
          style={styles.dismissButton}
          onPress={dismissPhoneCall}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="close" size={14} color={colors.text.secondary as string} />
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={styles.hangupButton}
          onPress={hangupPhoneCall}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="call" size={14} color="#FFFFFF" style={{ transform: [{ rotate: '135deg' }] }} />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    gap: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  avatar: { width: 32, height: 32, borderRadius: 16 },
  avatarFallback: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitials: {
    fontSize: typography.fontSize.xs,
    fontWeight: 'bold' as const,
    color: colors.primary as string,
  },
  info: { flex: 1 },
  name: {
    fontSize: typography.fontSize.sm,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
  },
  status: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary as string,
  },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  hangupButton: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#EF4444',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dismissButton: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: colors.border as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
